import { create } from 'zustand';
import {
  fetchDeleteUser,
  fetchGetUser,
  fetchCreateUser,
  fetchUpdateEmail,
} from '@/services/noteGoApi/noteGoApiComponents';
import { AccountStatusType, AccountStatusTypes, getStatus } from '@/helpers/accountStatus';
import { auth, db } from '@/firebase/firebase';
import { onAuthStateChanged, User, onIdTokenChanged } from 'firebase/auth';
import { doc, setDoc } from 'firebase/firestore';
import { RouteMap } from '@/routes/Routes';
import { syncAuthToExtension } from '@/utils/extensionAuthSync';
import { UserOut } from '@/services/noteGoApi/noteGoApiSchemas';
import { dripService } from '@/services/dripService';
import { errorReporter, isNetworkError } from '@/services/errorReportingService';
import { subscribeToLogout, isRecentLogout } from '@/utils/crossTabAuthSync';
import { clearQueryCache } from '@/services/queryClient';
import { asApiError, isUserNotFoundError } from '@/utils/apiErrorMessage';
import { logger } from '@/services/loggerService';
import { DEFAULT_FREE_SUBSCRIPTION } from '@/constants/subscriptionConstants';
import { getDeviceBrowserInfo } from '@/utils/deviceBrowserInfo';

/**
 * Redirect to login page, storing the current URL for post-login redirect.
 * Skips redirect if already on login page, on a share link, or on desktop-auth
 * (which needs unauthenticated access for OAuth handoff from desktop app).
 * Share links (`/public/:token`, `/shared/:token`) never need a session — the
 * `/shared/` page forwards signed-out viewers to `/public/` itself.
 */
const redirectToLogin = (): void => {
  const pathname = window.location.pathname;
  const isPublicRoute = pathname.startsWith('/public/') || pathname.startsWith('/shared/');
  const isDesktopAuth = pathname === RouteMap.DESKTOP_AUTH;
  if (pathname !== RouteMap.LOGIN && !isPublicRoute && !isDesktopAuth) {
    const currentUrl = pathname + window.location.search;
    sessionStorage.setItem('redirectAfterLogin', currentUrl);
    window.location.href = RouteMap.LOGIN;
  }
};

let tokenRetryPending = false;

/**
 * Retry a token refresh that failed on the network. A network failure is not an
 * invalid session — Firebase keeps the persisted user — so treating it as one sent
 * the viewer to /login, which resumed the session and sent them straight back: a
 * reload loop for as long as the connection stayed flaky. A successful refresh
 * fires onIdTokenChanged, which resumes the normal token + user-data path.
 *
 * @param user Firebase user whose token refresh failed
 * @param delayMs wait before this attempt; doubles per failure, capped at 30s
 */
const retryTokenRefresh = (user: User, delayMs = 2000): void => {
  if (tokenRetryPending) return;
  tokenRetryPending = true;
  window.setTimeout(() => {
    tokenRetryPending = false;
    // Stop once the session has ended or changed hands.
    if (auth.currentUser !== user) return;
    user.getIdToken(true).catch((error: unknown) => {
      if (isNetworkError(error)) retryTokenRefresh(user, Math.min(delayMs * 2, 30000));
    });
  }, delayMs);
};

/**
 * Record the authenticated user's device + browser on their Firestore user
 * document (and mirror them into Drip custom fields) so we can report on what
 * tech users are on. Fires at most once per browser session, never blocks the
 * auth flow, and silently no-ops on failure.
 *
 * @param userId backend user doc ID (`userData.id`) — the Firestore `users` doc key (CONTRACT #5)
 * @param email authenticated user's email — used to identify the Drip subscriber (optional)
 */
const recordUserTechProfile = (userId: string, email?: string | null): void => {
  const SESSION_FLAG = 'userTechProfileLogged';

  // Telemetry only — wrapped so a throwing sessionStorage (private mode,
  // sandboxed iframe, storage disabled) can never disrupt the auth flow this
  // runs inside (the fetchUserData `finally` block).
  try {
    if (sessionStorage.getItem(SESSION_FLAG) === 'true') return;
    // Set the flag up front so concurrent/rapid auth refreshes don't double-write.
    sessionStorage.setItem(SESSION_FLAG, 'true');

    const { deviceType, browser } = getDeviceBrowserInfo();

    // Mirror the same device/browser values into Drip custom fields so the
    // marketing audience can segment on tech. Fire-and-forget — never blocks auth.
    if (email) {
      dripService.updateDeviceBrowser(email, deviceType, browser);
    }

    setDoc(
      doc(db, 'users', userId),
      {
        device_type: deviceType,
        browser,
        last_login_at: new Date().toISOString(),
      },
      { merge: true },
    ).catch((error: unknown) => {
      // Never disrupt auth if the write fails. Clear the flag so a later fetch in
      // the same session can retry.
      try {
        sessionStorage.removeItem(SESSION_FLAG);
      } catch {
        // sessionStorage may be unavailable in some contexts
      }
      errorReporter.expected('Failed to record user device/browser profile', error, {
        feature: 'auth',
        operation: 'recordUserTechProfile',
      });
    });
  } catch {
    // sessionStorage may be unavailable in some contexts — telemetry is best-effort.
  }
};

interface FetchUserDataOptions {
  force?: boolean;
}

interface AuthState {
  currentUser: User | null;
  userLoggedIn: boolean;
  loading: boolean;
  userToken: string | null;
  userData: UserOut | null;
  accountStatus: AccountStatusType | null;
  fetchUserDataPromise: Promise<void> | null;
  lastUserDataFetch: number | null;
  /** Flag to block auth operations during logout - prevents race condition with extension auth */
  isLoggingOut: boolean;
  /** Flag to block auth initialization during signup - prevents race condition where onAuthStateChanged fires before displayName is set */
  isSigningUp: boolean;
  initializeUser: (user: User | null) => void;
  setLoading: (isLoading: boolean) => void;
  setUserToken: (token: string | null) => void;
  setIsLoggingOut: (value: boolean) => void;
  setIsSigningUp: (value: boolean) => void;
  fetchAndSetUserData: (options?: FetchUserDataOptions) => Promise<void>;
}

export const useAuthStore = create<AuthState>((set, get) => ({
  currentUser: null,
  userLoggedIn: false,
  loading: true,
  userToken: null,
  userData: null,
  accountStatus: null,
  fetchUserDataPromise: null,
  lastUserDataFetch: null,
  isLoggingOut: false,
  isSigningUp: false,
  initializeUser: (user) => {
    // Set user info immediately. If user exists, keep loading: true until token is ready.
    // If user is null, set loading: false immediately since there's nothing to wait for.
    set({
      currentUser: user,
      userLoggedIn: !!user,
      loading: !!user,
    });
    if (user) {
      user
        .getIdToken(true)
        .then((token: string) => {
          set({ userToken: token, loading: false });
          syncAuthToExtension(user, { reason: 'page-load', token }).catch((error) => {
            errorReporter.expected('Extension sync failed on page load', error, {
              feature: 'extension',
              operation: 'syncOnLoad',
            });
          });
          // Force refresh on initial load to ensure fresh data (e.g., after page refresh)
          get().fetchAndSetUserData({ force: true });
        })
        .catch((error) => {
          // Offline / flaky connection: keep the session and retry (see retryTokenRefresh).
          if (isNetworkError(error)) {
            errorReporter.expected('ID token refresh hit a network error on initialization', error, {
              feature: 'auth',
              operation: 'initializeUser',
            });
            set({ loading: false });
            retryTokenRefresh(user);
            return;
          }
          // Token fetch failed - reset auth state completely and redirect to login
          // This can happen if the Firebase session is invalid/expired
          errorReporter.critical('Failed to get ID token on initialization', error, {
            feature: 'auth',
            operation: 'initializeUser',
          });
          set({
            currentUser: null,
            userLoggedIn: false,
            userToken: null,
            userData: null,
            accountStatus: null,
            lastUserDataFetch: null,
            loading: false,
          });
          redirectToLogin();
        });
    } else {
      // Only meaningful if a session actually existed. On an anonymous page load
      // Firebase resolves persistence to `null` a few hundred ms after boot;
      // clearing then would destroy and re-issue in-flight public queries
      // (e.g. comments on /shared/:token) for no benefit.
      const hadSession = !!get().userToken || !!get().userData;

      // User is null - clear all auth-related state. loading: false is already set above via `loading: !!user`
      set({ userToken: null, userData: null, accountStatus: null, lastUserDataFetch: null });

      // Purge cached server state here too, not only at the start of logout.
      // The pre-signOut clear runs while the outgoing token is still valid, so
      // any component still mounted (e.g. Settings during Delete Account)
      // immediately refetches and repopulates the cache with the departing
      // user's data. This one runs after Firebase has dropped the session, so
      // it is the clear that sticks.
      if (hadSession) {
        clearQueryCache();
      }
    }
  },

  setLoading: (isLoading) => set({ loading: isLoading }),

  setUserToken: (token) => set({ userToken: token }),

  setIsLoggingOut: (value) => set({ isLoggingOut: value }),

  setIsSigningUp: (value) => set({ isSigningUp: value }),

  fetchAndSetUserData: async ({ force = false }: FetchUserDataOptions = {}) => {
    const state = get();
    const { userToken, currentUser, userData, lastUserDataFetch } = state;

    // If there's already a fetch in progress, return the existing promise
    // This prevents duplicate concurrent requests - checked FIRST to prevent race conditions
    if (state.fetchUserDataPromise) {
      return state.fetchUserDataPromise;
    }

    if (!userToken || !currentUser) return;

    // Cache expiration: configurable via VITE_USER_DATA_CACHE_MS environment variable (milliseconds)
    // Default: 5 minutes (300,000 ms)
    const CACHE_EXPIRATION_MS = Number(import.meta.env.VITE_USER_DATA_CACHE_MS) || 5 * 60 * 1000;
    // sameUser guard: only use cached userData if it belongs to the current login (prevents stale flag after account switch)
    // Compare against firebase_uid — userData.id is the backend doc ID, not the Firebase Auth UID (see CONTRACT #5)
    const isSameUser = userData?.firebase_uid === currentUser.uid;
    if (!force && userData && isSameUser && lastUserDataFetch && Date.now() - lastUserDataFetch < CACHE_EXPIRATION_MS) {
      return Promise.resolve();
    }
    // If user mismatch detected, clear stale cache
    if (userData && !isSameUser) {
      set({ userData: null, lastUserDataFetch: null });
    }

    // Create the promise and store it immediately to prevent race conditions
    const promise = (async () => {
      try {
        const userData = await fetchGetUser({
          headers: { authorization: `Bearer ${userToken}`, 'Cache-Control': 'no-cache' },
        });
        set({ userData, lastUserDataFetch: Date.now() });

        // Check if user exists but doesn't have email - migration for existing users
        if (!userData?.email && currentUser?.email) {
          try {
            await fetchUpdateEmail({
              headers: { authorization: `Bearer ${userToken}` },
              body: {
                email: currentUser.email,
              },
            });
            // Refetch user data to get updated email
            const updatedUserData = await fetchGetUser({
              headers: { authorization: `Bearer ${userToken}`, 'Cache-Control': 'no-cache' },
            });
            set({ userData: updatedUserData, lastUserDataFetch: Date.now() });
          } catch (emailUpdateError) {
            // Expected: Email migration can fail for various reasons (network, permissions)
            // User can still use the app, email will be synced later
            errorReporter.expected('Failed to migrate email for existing user', emailUpdateError, {
              feature: 'auth',
              operation: 'emailMigration',
            });
          }
        }

        const accountStatus = getStatus(userData?.deletion_date, userData?.subscription?.status);

        if (accountStatus === AccountStatusTypes.DELETED) {
          await fetchDeleteUser({ headers: { authorization: `Bearer ${userToken}` } });
          await auth.signOut();
          window.location.href = RouteMap.LOGIN;
        }

        set({ accountStatus });

        // Tag user in Drip after successful authentication
        if (currentUser?.email) {
          const planName = userData?.subscription?.product_name || 'Free';
          dripService.tagAsNoteGoUser(currentUser.email, planName);
        }
      } catch (caught: unknown) {
        const error = asApiError(caught);
        // Log sanitized error for debugging (avoid exposing sensitive data in production)
        const safeErrorInfo = {
          name: error.name,
          message: error.message,
          code: error.code,
          status: error.status,
          detail: error.detail,
        };
        logger.warn('auth', 'fetchAndSetUserData error', { message: error.message });
        logger.debug('auth', 'fetchAndSetUserData error details', safeErrorInfo);

        // If user not found (404), create them in the backend
        if (isUserNotFoundError(caught) && currentUser) {
          try {
            const nameParts = currentUser.displayName?.split(' ') || ['', ''];
            await fetchCreateUser({
              headers: { authorization: `Bearer ${userToken}` },
              body: {
                first_name: nameParts[0] || '',
                last_name: nameParts.slice(1).join(' ') || '',
                phone_number: currentUser.phoneNumber || '',
                firebase_uid: currentUser.uid, // Pass Firebase UID to prevent duplicate user documents
                // Include default subscription to prevent AI processing failures
                // Backend requires subscription.plan to be set for AI worker to process media
                subscription: DEFAULT_FREE_SUBSCRIPTION,
              },
            });

            // Update the email separately since UserCreate doesn't have an email field
            if (currentUser.email) {
              await fetchUpdateEmail({
                headers: { authorization: `Bearer ${userToken}` },
                body: {
                  email: currentUser.email,
                },
              });
            }

            // Try fetching user data again after creation
            const newUserData = await fetchGetUser({
              headers: { authorization: `Bearer ${userToken}`, 'Cache-Control': 'no-cache' },
            });
            set({ userData: newUserData, lastUserDataFetch: Date.now() });

            const accountStatus = getStatus(newUserData?.deletion_date, newUserData?.subscription?.status);
            set({ accountStatus });

            // Tag new user in Drip
            if (currentUser?.email) {
              const planName = newUserData?.subscription?.product_name || 'Free';
              dripService.tagAsNoteGoUser(currentUser.email, planName);
            }
          } catch (createError) {
            errorReporter.critical('Failed to create user in backend', createError, {
              feature: 'auth',
              operation: 'createUser',
              firebaseUid: currentUser.uid,
            });
            set({ userData: null, accountStatus: AccountStatusTypes.TRIAL, lastUserDataFetch: null });
          }
        } else {
          // Transient network failures (common on mobile) are expected — don't alert Sentry
          const errorMsg = String(error.message ?? '').toLowerCase();
          const isNetworkError =
            errorMsg.includes('network error') ||
            errorMsg.includes('load failed') ||
            errorMsg.includes('failed to fetch') ||
            errorMsg.includes('networkerror') ||
            errorMsg.includes('timeout');

          if (isNetworkError) {
            errorReporter.expected('Failed to fetch user data (network error)', error, {
              feature: 'auth',
              operation: 'fetchUserData',
              errorMessage: error.message,
            });
            // Preserve last-known-good userData on transient network failures. Wiping it would
            // strand mobile users mid-flow (e.g., a recording upload that needs userData.id)
            // when cellular hiccups cause a refetch to fail. Only applies to network errors —
            // auth failures (401/403) always clear userData below.
            // Always invalidate the cache timestamp so the next non-forced fetch retries
            // immediately instead of returning stale data for up to CACHE_EXPIRATION_MS.
            if (!userData) {
              set({ userData: null, accountStatus: AccountStatusTypes.TRIAL, lastUserDataFetch: null });
            } else {
              set({ lastUserDataFetch: null });
            }
          } else {
            errorReporter.critical('Failed to fetch user data (non-404 error)', error, {
              feature: 'auth',
              operation: 'fetchUserData',
              errorStatus: error.status,
              errorMessage: error.message,
            });
            set({ userData: null, accountStatus: AccountStatusTypes.TRIAL, lastUserDataFetch: null });
          }
        }
      } finally {
        // Clear the promise when done
        set({ fetchUserDataPromise: null });

        // After the fetch settles, if we have a user id, record their device/browser
        // for tech reporting. Guarded to once per session and fully fire-and-forget —
        // never blocks auth (runs after the promise is cleared above).
        const loggedInUserId = get().userData?.id;
        if (loggedInUserId) {
          recordUserTechProfile(loggedInUserId, get().currentUser?.email);
        }
      }
    })();

    // Store the promise immediately (before any await) to prevent concurrent calls
    set({ fetchUserDataPromise: promise });
    return promise;
  },
}));

// Firebase authentication listeners
onAuthStateChanged(auth, (user) => {
  const { initializeUser, setLoading, setIsLoggingOut, isLoggingOut, isSigningUp } = useAuthStore.getState();

  if (isSigningUp && user) {
    logger.debug('auth', 'Signup in progress - skipping user initialization in onAuthStateChanged');
    return;
  }

  // CRITICAL: Check if this is a logout request BEFORE initializing user
  // This prevents the race condition where Firebase's persisted auth fires
  // before the Login component can process the ?logout=true param
  // Note: We only check the URL param here, not isRecentLogout(), because:
  // 1. Cross-tab logout is handled separately by subscribeToLogout()
  // 2. isRecentLogout() would block immediate re-login attempts within 10 seconds
  const isLogoutRequest =
    window.location.search.includes('logout=true') || sessionStorage.getItem('userRequestedLogout') === 'true';

  if (isLogoutRequest && user) {
    logger.debug('auth', 'Logout request detected in onAuthStateChanged - skipping user initialization');
    // Set the flag to block other auth operations
    setIsLoggingOut(true);
    // Purge cached server state so it can't be served to the next account (see queryClient.ts)
    clearQueryCache();
    // Sign out Firebase and then clean up state
    auth
      .signOut()
      .catch((error) => {
        errorReporter.critical('Firebase signOut failed in onAuthStateChanged', error, {
          feature: 'auth',
          operation: 'signOutOnLogoutRequest',
        });
        // Continue with local state cleanup even if Firebase signOut fails
      })
      .finally(() => {
        setIsLoggingOut(false);
        // Clear the sessionStorage flag now that logout is complete.
        // This allows Login's redirect effect to proceed on the next login attempt.
        sessionStorage.removeItem('userRequestedLogout');
        // Initialize with null user to ensure clean state
        initializeUser(null);
        setLoading(false);
      });
    return;
  }

  // If we're logging out and user is now null, logout completed successfully
  // This is when we should reset the isLoggingOut flag (not in doSignOut's finally block)
  // to prevent race conditions with extension auto-login
  if (isLoggingOut && !user) {
    logger.debug('auth', 'Logout completed - user is null, resetting isLoggingOut flag');
    setIsLoggingOut(false);
    // Clear the sessionStorage flag now that logout is complete.
    // This allows Login's redirect effect to proceed on the next login attempt.
    sessionStorage.removeItem('userRequestedLogout');
  }

  initializeUser(user);
  setLoading(false);
});

// Cross-tab logout synchronization
// When another tab logs out, this tab will receive a 'storage' event
// and should also sign out to maintain consistent state
const unsubscribeLogout = subscribeToLogout(() => {
  const { initializeUser, setLoading, setIsLoggingOut } = useAuthStore.getState();

  logger.debug('auth', 'Logout detected from another tab - signing out');

  // Set the flag to block concurrent auth operations
  setIsLoggingOut(true);
  // Purge cached server state so it can't be served to the next account (see queryClient.ts)
  clearQueryCache();

  // Sign out Firebase in this tab and clean up state after completion
  auth
    .signOut()
    .catch((error) => {
      errorReporter.critical('Firebase signOut failed during cross-tab sync', error, {
        feature: 'auth',
        operation: 'crossTabSignOut',
      });
      // Continue with local state cleanup even if Firebase signOut fails
    })
    .finally(() => {
      setIsLoggingOut(false);
      // Initialize with null user to ensure clean state
      initializeUser(null);
      setLoading(false);

      // Redirect using shared helper (respects public routes and desktop-auth)
      redirectToLogin();
    });
});

// Cleanup on HMR to prevent duplicate listeners in development
if (import.meta.hot) {
  import.meta.hot.dispose(() => {
    unsubscribeLogout();
  });
}

onIdTokenChanged(auth, async (user) => {
  const { setUserToken, fetchAndSetUserData, userToken, isLoggingOut, isSigningUp } = useAuthStore.getState();

  // Skip any auth operations during logout to prevent race conditions
  // Check both the store flag AND the localStorage timestamp flag as defense-in-depth
  if (isLoggingOut || isRecentLogout()) {
    logger.debug('auth', 'Skipping onIdTokenChanged - logout in progress', {
      isLoggingOut,
      isRecentLogout: isRecentLogout(),
    });
    return;
  }

  // Skip any auth operations during signup to prevent race conditions
  // The signup flow handles user creation with proper displayName
  if (isSigningUp) {
    logger.debug('auth', 'Skipping onIdTokenChanged - signup in progress');
    return;
  }

  if (user) {
    try {
      const token = await user.getIdToken(); // Don't force refresh - Firebase handles this automatically
      // Only update if token actually changed to avoid unnecessary re-renders and API calls
      if (token !== userToken) {
        setUserToken(token);
        syncAuthToExtension(user, { reason: 'token-refresh', token }).catch((error) => {
          errorReporter.expected('Extension sync failed on token refresh', error, {
            feature: 'extension',
            operation: 'syncOnTokenRefresh',
          });
        });
        // Only fetch user data if we don't have it yet (not on every token refresh)
        // The cache in fetchAndSetUserData handles staleness
        fetchAndSetUserData();
      }
    } catch (error) {
      // Offline / flaky connection: keep the session and retry (see retryTokenRefresh).
      if (isNetworkError(error)) {
        errorReporter.expected('Token refresh hit a network error in onIdTokenChanged', error, {
          feature: 'auth',
          operation: 'tokenRefresh',
        });
        retryTokenRefresh(user);
        return;
      }
      // Token refresh failed - redirect to login
      errorReporter.critical('Token refresh failed in onIdTokenChanged', error, {
        feature: 'auth',
        operation: 'tokenRefresh',
      });
      setUserToken(null);
      redirectToLogin();
    }
  } else {
    setUserToken(null);
    redirectToLogin();
  }
});

// Note: Token refresh is handled automatically by Firebase's onIdTokenChanged listener above.
// Firebase refreshes tokens ~5 minutes before expiration (1 hour default TTL).
// No need for a manual setInterval - it was redundant and caused unnecessary API calls.
